/**
 * AddItemForm — Plan 04-03 / UI-SPEC §"<AddItemForm />".
 *
 * Ett tekstfelt; Enter eller den runde +-knappen sender. UI-SPEC-kontrakt:
 *   - Empty submit triggers `animate-shake` for ~300ms; no network call.
 *   - Submit fires `addItem.mutate({id: crypto.randomUUID(), text})` —
 *     client-generated UUID per D-09 so an offline-create round-trips.
 *   - Tablet: auto-focus on mount (kitchen-tablet primary use case).
 *   - Phone: no auto-focus (avoids surprise on-screen keyboard in-store).
 *
 * Tablet detection: `window.matchMedia('(min-width: 1024px)')` mirrors
 * Tailwind v4's `lg:` breakpoint (the @theme uses default `screens`).
 *
 * data-testids: shopping-add-input, shopping-add-submit.
 *
 * T-04-04: `maxLength={200}` on the input matches the server-side Zod
 * `text` cap; UI never sends more than 200 chars.
 *
 * #157 (redesign steg 7d, design/screens/Stell_Handle.dc.html): feltet er
 * `field` (52 px), og den runde +-knappen (52 px, `bg-primary`) står ved
 * siden av på alle bredder, ikke bare på nettbrett. Enter sender fortsatt.
 * Ristingen ligger på omslaget rundt skjemaet, som før på kortet.
 */
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/primitives/Icon';
import { useShoppingMutations } from './useShoppingMutations';

const MAX_TEXT_LEN = 200; // matches server-side Zod text cap (T-04-04)
const SHAKE_MS = 320;

function isTabletViewport(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(min-width: 1024px)').matches;
}

export function AddItemForm() {
  const { t } = useTranslation();
  const { addItem } = useShoppingMutations();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [text, setText] = useState('');
  const [shaking, setShaking] = useState(false);
  const [tablet] = useState<boolean>(() => isTabletViewport());

  // Tablet: auto-focus on mount. Phone: no auto-focus.
  useEffect(() => {
    if (tablet) inputRef.current?.focus();
  }, [tablet]);

  function triggerShake() {
    setShaking(true);
    window.setTimeout(() => setShaking(false), SHAKE_MS);
  }

  function onSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const trimmed = text.trim();
    if (trimmed.length === 0) {
      triggerShake();
      return;
    }
    addItem.mutate({
      id: crypto.randomUUID(),
      text: trimmed,
    });
    setText('');
  }

  return (
    <div className={shaking ? 'animate-shake' : undefined}>
      <form onSubmit={onSubmit} className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          data-testid="shopping-add-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('shopping.add.placeholder')}
          aria-label={t('shopping.add.submit_aria')}
          maxLength={MAX_TEXT_LEN}
          className="field min-w-0 flex-1 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
        />
        <button
          type="submit"
          data-testid="shopping-add-submit"
          aria-label={t('shopping.add.submit_aria')}
          className="flex h-13 w-13 shrink-0 items-center justify-center rounded-full bg-primary text-on-primary hover:opacity-90"
        >
          <Icon.Plus size={22} />
        </button>
      </form>
    </div>
  );
}
